/**
 * AgenticV2 — v0 redesign anchor.
 *
 * Hosts the new In Scope / Available scope panel (A) on the left rail
 * and the chat column on the right. Card shell (B) and result lift (D)
 * are subsequent surfaces in the redesign sequence; trace UI (C) follows.
 *
 * Spec: v2-design/SCOPE_PANEL_v0_spec.md and siblings.
 *
 * Client context: clientId is read from URL ?client=, falling back to
 * profile.User.ClientID when the user is bound to a single client.
 * Without a clientId the panel shows an empty state.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import { Navigate, useLocation } from 'react-router-dom';

import { HTTPError } from 'src/support/errors';
import { useProfile } from 'src/hooks/useProfile';
import { parseId } from 'src/utils/parsing';
import { isTrinityUser } from 'src/utils/user';

import { CardShell, expectedStatusForCard } from './cards/shell/CardShell';
import { createSession, postMessage, resolveCard, ScopeEnvelope } from './api';
import { DagProgress } from './DagProgress';
import { CalcStatus } from './CalcStatus';
import { MarkdownMessage } from './MarkdownMessage';
import { ScopePanel } from './scope/ScopePanel';
import { applyFinalResponse, applyStreamEvent } from './streamReducer';
import { CardType, ResolveAction, StreamEvent, Turn } from './types';
import SaveToLibraryDialog from './library/SaveToLibraryDialog';
import LibraryManagerModal from './library/LibraryManagerModal';

// ---- panel width bounds + persistence ----
const PANEL_WIDTH_KEY = 'agenticv2.scopePanelWidth';
const PANEL_MIN_WIDTH = 180;
const PANEL_MAX_WIDTH = 480;
const PANEL_DEFAULT_WIDTH = 280;

function readPersistedPanelWidth(): number {
  if (typeof window === 'undefined') return PANEL_DEFAULT_WIDTH;
  try {
    const raw = window.localStorage.getItem(PANEL_WIDTH_KEY);
    if (!raw) return PANEL_DEFAULT_WIDTH;
    const n = Number(raw);
    if (!Number.isFinite(n)) return PANEL_DEFAULT_WIDTH;
    return clampPanelWidth(n);
  } catch {
    return PANEL_DEFAULT_WIDTH;
  }
}

function clampPanelWidth(n: number): number {
  return Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, Math.round(n)));
}

interface IAgenticV2Props {
  // Rendered inside ForecastEdgeAITab's toggle layout: fill the parent
  // box instead of sizing against the viewport.
  embedded?: boolean;
  // Analyst/Executive surface toggle, owned by ForecastEdgeAITab.
  // Rendered title-adjacent in the header row — it's a surface-mode
  // control, so it rides with the surface's name, apart from the
  // session controls (Executive Library / New Chat) on the right.
  // Absent for analyst-only users, who render AgenticV2 directly.
  surfaceToggle?: React.ReactNode;
}

const AgenticV2: React.FC<IAgenticV2Props> = ({
  embedded = false,
  surfaceToggle,
}) => {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScopeState] = useState<ScopeEnvelope | null>(null);
  // Executive-library authoring (Trinity users only).
  const [saveDialogPlanId, setSaveDialogPlanId] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  // Per-tab dialogue session. Null until POST /v2/sessions returns —
  // the backend mints the id via SQL NEWID(), the FE never generates it.
  // Re-fires on clientId change (each AgentSession is anchored to a single
  // client at create time). Held in component state, not localStorage;
  // refresh = new session by design.
  const [sessionId, setSessionId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // ---- panel width (drag-resizable, persisted) ----
  const [panelWidth, setPanelWidth] = useState<number>(readPersistedPanelWidth);
  const [panelExpanded, setPanelExpanded] = useState<boolean>(true);
  const draggingRef = useRef(false);
  const layoutRef = useRef<HTMLDivElement | null>(null);

  // Chat column ref retained for future use (drag handle bounds, etc.).
  const chatColumnRef = useRef<HTMLDivElement | null>(null);

  // Persist on mouseup, not every drag frame, to avoid localStorage thrashing.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!panelWidth) return;
    // Defer persist to a microtask so we batch with React's render commit.
    const id = setTimeout(() => {
      try {
        window.localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth));
      } catch {
        // ignore quota / disabled-storage cases
      }
    }, 0);
    return () => clearTimeout(id);
  }, [panelWidth]);

  const handleDragStart = useCallback(
    (e: React.PointerEvent) => {
      if (!panelExpanded) return;
      e.preventDefault();
      draggingRef.current = true;
      (e.target as Element).setPointerCapture?.(e.pointerId);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [panelExpanded]
  );

  const handleDragMove = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    if (!layoutRef.current) return;
    const layoutLeft = layoutRef.current.getBoundingClientRect().left;
    const next = clampPanelWidth(e.clientX - layoutLeft);
    setPanelWidth(next);
  }, []);

  const handleDragEnd = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try {
      (e.target as Element).releasePointerCapture?.(e.pointerId);
    } catch {
      // pointer might already be released
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // ---- clientId resolution ----
  // profile.User.ClientID (if user is bound to one client) overrides
  // URL ?client= so a single-client user can't accidentally browse
  // another's scope tree; otherwise URL ?client= is the source of truth.
  const profile = useProfile();
  const location = useLocation();
  // Executive-library authoring gate: Trinity users only (the tab itself
  // already requires HasAgenticAccess).
  const canAuthorLibrary = profile?.User != null && isTrinityUser(profile.User);
  const clientId = useMemo<number | null>(() => {
    if (
      profile?.User?.ClientID !== null &&
      profile?.User?.ClientID !== undefined
    ) {
      return profile.User.ClientID;
    }
    const params = new URLSearchParams(location.search);
    return parseId(params.get('client')) ?? null;
  }, [profile, location.search]);

  // Mint a fresh AgentSession on mount, and again whenever the active
  // client changes. The `cancelled` guard handles a rapid client switch
  // where two creates are in flight — only the latest survives.
  useEffect(() => {
    if (clientId == null) return undefined;
    let cancelled = false;
    setSessionId(null);
    void createSession(clientId)
      .then((newId) => {
        if (!cancelled) setSessionId(newId);
      })
      .catch((e) => {
        if (!cancelled) setError(formatError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  // Scroll to newest turn whenever the list grows.
  useEffect(() => {
    scrollToBottom();
  }, [turns, scrollToBottom]);

  // Stream-event sink shared by handleSend and handleResolve. Turns arrive
  // as row_inserted / row_updated events on the NDJSON channel; the reducer
  // is the single source of truth for the chat view-model.
  const handleStreamEvent = useCallback((event: StreamEvent) => {
    setTurns((prev) => applyStreamEvent(prev, event));
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || busy || sessionId == null) return;
    setBusy(true);
    setError(null);
    setInput('');
    // Scroll immediately on send. The user-text row_inserted event
    // arrives ~hundreds-of-ms later and the [turns] effect will scroll
    // again then; this initial nudge ensures the user's intent ("I just
    // hit Send") is reflected in the scroll position right away, even
    // when the chat history is tall enough that the input was the only
    // thing on screen.
    scrollToBottom();
    try {
      const resp = await postMessage(text, sessionId, handleStreamEvent);
      setTurns((prev) => applyFinalResponse(prev, resp));
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }, [busy, handleStreamEvent, input, scrollToBottom, sessionId]);

  const handleResolve = useCallback(
    async (
      turnIndex: number,
      planId: string,
      cardType: CardType,
      action: ResolveAction
    ) => {
      if (busy || sessionId == null) return;
      const expected = expectedStatusForCard(cardType);
      if (!expected) {
        setError(
          `Cannot resolve card type ${cardType}: unknown expected status.`
        );
        return;
      }
      setBusy(true);
      setError(null);
      // Mark the originating card as resolved up front so its CardShell
      // goes into the de-emphasized treatment immediately. Streamed
      // subsequent rows append below it.
      const resolvedLabel = resolvedLabelForAction(action.action);
      setTurns((prev) =>
        prev.map((t, i) =>
          i === turnIndex && t.kind === 'card'
            ? { ...t, resolved: resolvedLabel }
            : t
        )
      );
      try {
        const resp = await resolveCard(
          planId,
          expected,
          action,
          sessionId,
          handleStreamEvent
        );
        setTurns((prev) => applyFinalResponse(prev, resp));
      } catch (e) {
        setError(formatError(e));
      } finally {
        setBusy(false);
      }
    },
    [busy, handleStreamEvent, sessionId]
  );

  const handleNewSession = useCallback(async () => {
    // Aggressive reset: clear every piece of in-tab state tied to the
    // previous dialogue, then mint a fresh AgentSession. setSessionId(null)
    // up front unmounts the ScopePanel (via key={sessionId}); the new id
    // remounts it with a fresh tree from the backend.
    if (busy || clientId == null) return;
    setSessionId(null);
    setTurns([]);
    setInput('');
    setError(null);
    setScopeState(null);
    try {
      const newId = await createSession(clientId);
      setSessionId(newId);
    } catch (e) {
      setError(formatError(e));
    }
  }, [busy, clientId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const scopeInForce = (scope?.entries.length ?? 0) > 0;

  if (profile?.User?.HasAgenticAccess !== 1) {
    return <Navigate to="/scenario-manager" replace />;
  }

  return (
    <Box
      ref={layoutRef}
      sx={{
        display: 'flex',
        height: embedded ? '100%' : 'calc(100vh - 120px)',
        // Without these the chat column's children (especially the
        // overflowY:auto Messages box) push the outer layout past the
        // fixed height and the entire body grows — defeating the inner
        // scroll container. The pair of overflow:hidden + minHeight:0
        // is the standard flex-clipping idiom; both are needed.
        overflow: 'hidden',
        minHeight: 0,
        py: 2,
      }}
      onPointerMove={handleDragMove}
      onPointerUp={handleDragEnd}
      onPointerCancel={handleDragEnd}
    >
      {/* Left rail — scope panel (A). Gated on BOTH clientId and
          sessionId: ScopePanel's first action is listScopeOptions, which
          requires a live session_id. While waiting for POST /v2/sessions
          to return we show a thin placeholder rail. */}
      {clientId !== null && sessionId !== null ? (
        <ScopePanel
          key={sessionId}
          sessionId={sessionId}
          clientId={clientId}
          busy={busy}
          expandedWidth={panelWidth}
          onExpandedChange={setPanelExpanded}
          onScopeChange={setScopeState}
        />
      ) : (
        <Box
          sx={{
            width: panelWidth,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'grey.200',
            p: 1.5,
          }}
        >
          {clientId === null ? (
            <Typography variant="caption" color="text.secondary">
              No client selected. Open this tab with{' '}
              <code>?client=&lt;id&gt;</code> or via the client picker.
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Initializing dialogue…
              </Typography>
            </Stack>
          )}
        </Box>
      )}

      {/* Drag handle — only active when panel is expanded. Hit area is
          6px wide but the visible band is 1px so it doesn't compete with
          the wrapper-edge frame. */}
      {panelExpanded && (
        <Box
          onPointerDown={handleDragStart}
          sx={{
            width: 6,
            ml: '-3px',
            mr: '-3px',
            flexShrink: 0,
            cursor: 'col-resize',
            zIndex: 1,
            position: 'relative',
            // On hover, show a subtle accent so the handle is discoverable.
            '&:hover::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 2,
              bgcolor: 'primary.light',
              opacity: 0.6,
            },
          }}
        />
      )}

      {/* Main chat column — anchored flush against the panel's right
          edge so the wrapper-edge frame "kisses" the panel.

          The chat-side left accent is a 2px-wide pseudo-element with
          a linear-gradient: the top portion (overlapping the In Scope
          bracket vertically) is muted, the bottom portion (below the
          bracket) is full primary intensity. Transition Y is measured
          from the In Scope frame's rendered bottom and updated via
          ResizeObserver.

          The top/bottom horizontal rules are intentionally quieter
          than the panel-side bracket (1px at 30% alpha) so they read
          as atmospheric "scope is in force" cues rather than as a
          competing frame around chat content. */}
      <Box
        ref={chatColumnRef}
        sx={{
          flex: 1,
          minWidth: 0,
          // minHeight:0 is required for nested flex children with
          // overflow:auto to actually clip (see outer Box comment).
          minHeight: 0,
          height: '100%',
          position: 'relative',
          ...(scopeInForce && {
            // Quiet 1px @ 30% alpha so the band reads as atmospheric.
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: (theme) => `${theme.palette.primary.main}4D`, // 30%
            bgcolor: (theme) => `${theme.palette.primary.main}0A`, // 4%
            // Vertical accent stripe at 30% alpha throughout (matching
            // the horizontal rules and the panel-side In Scope frame).
            // No intensity-flip below the In Scope overlap — one
            // consistent quiet tone across the whole treatment.
            // 1px wide left-edge accent stripe. NOTE: MUI sx interprets
            // numeric width as a fraction of the parent (width: 1 = 100%),
            // so the px value must be a string. This bit me once already.
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: '1px',
              pointerEvents: 'none',
              bgcolor: (theme) => `${theme.palette.primary.main}4D`,
            },
          }),
          // Breathing room so chat content doesn't kiss the top/bottom
          // rules of the wrapper-edge band.
          px: 3,
          pt: 2,
          pb: 2,
        }}
      >
        <Stack
          direction="column"
          spacing={1.5}
          sx={{
            height: '100%',
            // Required so the Messages box (flex:1, overflowY:auto) clips
            // instead of pushing the column past its parent's height.
            minHeight: 0,
            maxWidth: 1100,
            // Center within the chat column on wide viewports so the
            // tinted band reads as intentional letterboxing rather than
            // a content chop-off on the right.
            mx: 'auto',
            width: '100%',
          }}
        >
          {/* Header: identity on the left (title + surface toggle),
              session controls on the right. The spacer Box carries the
              flex:1 the title used to hold. */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              ForecastEDGE AI
            </Typography>
            {surfaceToggle}
            <Box sx={{ flex: 1 }} />
            {canAuthorLibrary && (
              <Button
                variant="text"
                size="small"
                onClick={() => setLibraryOpen(true)}
                disabled={sessionId == null || clientId == null}
                sx={{ flexShrink: 0, textTransform: 'none' }}
              >
                Executive Library
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={handleNewSession}
              disabled={busy || sessionId == null || clientId == null}
              sx={{ flexShrink: 0, textTransform: 'none' }}
            >
              New Chat
            </Button>
          </Stack>

          {/* Messages */}
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 1,
              p: 2,
              bgcolor: 'background.paper',
            }}
          >
            {turns.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                Start the conversation. Try: <em>show me Net Sales</em> or{' '}
                <em>what can you do?</em>
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {turns.map((turn, i) => {
                  // Past-state filtering. Hide only fires once a
                  // successor has actually landed in the turn list (a
                  // newer card emission for the same plan, or a DAG row
                  // taking over from an Enumerator card). Until then,
                  // a resolved card stays visible in past-state styling
                  // so Confirm / Edit doesn't leave a visual hole.
                  let pastGate = false;
                  let forceCollapsed = false;
                  if (turn.kind === 'card') {
                    const newerSamePlan = hasNewerCardForPlan(turns, i);
                    const newerGateCard = hasNewerGateCard(turns, i);
                    pastGate = newerSamePlan || newerGateCard;
                    const newerSameType = hasNewerSameTypeCardForPlan(turns, i);
                    const newerDag = hasDagProgressForPlan(turns, i);
                    const isPastState = !!turn.resolved || pastGate;
                    if (isPastState) {
                      const vis = pastVisibilityForCard(
                        turn.cardType,
                        turn.resolved,
                        newerSamePlan,
                        newerSameType,
                        newerDag,
                        newerGateCard
                      );
                      if (vis === 'hidden') return null;
                      forceCollapsed = vis === 'collapsed';
                    }
                  }
                  if (turn.kind === 'calc_progress') {
                    // Hide once any subsequent card for this plan
                    // lands — the tracker has done its job. Covers
                    // both the ES Gate 1 indeterminate tracker
                    // (hidden by the change_review card) and the
                    // Gate 2 per-RI tracker (hidden by result_card).
                    if (hasNewerCardForCalcProgress(turns, i)) return null;
                  }
                  // "Save to prompt library" on completed CV result cards
                  // (Trinity authoring affordance; the backend re-verifies
                  // plan ownership, tool, and completed status).
                  const saveToLibraryAction =
                    canAuthorLibrary &&
                    turn.kind === 'card' &&
                    turn.cardType === 'result_card' &&
                    turn.planId &&
                    (turn.content as { tool?: string }).tool ===
                      'chart_visualization' ? (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<BookmarkAddOutlinedIcon />}
                        onClick={() =>
                          setSaveDialogPlanId(turn.planId as string)
                        }
                        sx={{ textTransform: 'none' }}
                      >
                        Save to prompt library
                      </Button>
                    ) : undefined;
                  return (
                    <TurnView
                      key={`${turn.ts}-${i}`}
                      turn={turn}
                      isPastGate={pastGate}
                      forceCollapsed={forceCollapsed}
                      extraActions={saveToLibraryAction}
                      onResolve={async (action) => {
                        if (turn.kind !== 'card') return;
                        // Ambiguity cards have no plan — route the
                        // selected candidate's label as a fresh text
                        // message back into the conversation.
                        if (
                          turn.cardType === 'ambiguity_card' &&
                          action.action === 'edit_field' &&
                          'field_updates' in action
                        ) {
                          const label =
                            action.field_updates?.__ambiguity_select;
                          if (
                            typeof label === 'string' &&
                            label &&
                            sessionId &&
                            !busy
                          ) {
                            setBusy(true);
                            setError(null);
                            setTurns((prev) =>
                              prev.map((t, idx) =>
                                idx === i && t.kind === 'card'
                                  ? { ...t, resolved: 'confirmed' as const }
                                  : t
                              )
                            );
                            try {
                              const resp = await postMessage(
                                `Use: ${label}`,
                                sessionId,
                                handleStreamEvent
                              );
                              setTurns((prev) =>
                                applyFinalResponse(prev, resp)
                              );
                            } catch (e) {
                              setError(formatError(e));
                            } finally {
                              setBusy(false);
                            }
                          }
                          return;
                        }
                        if (!turn.planId) return;
                        handleResolve(i, turn.planId, turn.cardType, action);
                      }}
                      busy={busy}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Errors — severity="warning" (amber): transient agent/service
              failures read as "retry", not as an application fault. */}
          {error && (
            <Alert severity="warning" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Input */}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder={
                sessionId == null ? 'Initializing dialogue…' : 'Type a message…'
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={busy || sessionId == null}
            />
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={busy || !input.trim() || sessionId == null}
              startIcon={
                busy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              sx={{ flexShrink: 0, height: 40, textTransform: 'none' }}
            >
              Send
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Executive-library authoring dialogs (Trinity users only). */}
      {canAuthorLibrary && (
        <>
          <SaveToLibraryDialog
            planId={saveDialogPlanId}
            onClose={() => setSaveDialogPlanId(null)}
          />
          <LibraryManagerModal
            open={libraryOpen}
            onClose={() => setLibraryOpen(false)}
            clientId={clientId}
            sessionId={sessionId}
          />
        </>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Turn rendering
// ---------------------------------------------------------------------------
interface TurnViewProps {
  turn: Turn;
  busy: boolean;
  isPastGate: boolean;
  forceCollapsed?: boolean;
  extraActions?: React.ReactNode;
  onResolve: (action: ResolveAction) => void;
}

const TurnView: React.FC<TurnViewProps> = ({
  turn,
  busy,
  isPastGate,
  forceCollapsed,
  extraActions,
  onResolve,
}) => {
  if (turn.kind === 'user') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box
          sx={{
            maxWidth: '80%',
            bgcolor: 'grey.100',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {turn.text}
          </Typography>
        </Box>
      </Box>
    );
  }
  if (turn.kind === 'assistant_text') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Box
          sx={{
            maxWidth: '80%',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
          }}
        >
          <MarkdownMessage content={turn.text} />
        </Box>
      </Box>
    );
  }
  if (turn.kind === 'system') {
    return (
      <Alert
        severity={turn.level === 'error' ? 'error' : 'info'}
        sx={{ py: 0 }}
      >
        {turn.text}
      </Alert>
    );
  }
  if (turn.kind === 'dag_progress') {
    return <DagProgress stages={turn.stages} />;
  }
  if (turn.kind === 'calc_progress') {
    return <CalcStatus title={turn.title} rows={turn.rows} />;
  }
  // Card: delegate to CardShell.
  return (
    <CardShell
      cardType={turn.cardType}
      content={turn.content}
      planId={turn.planId ?? null}
      planStatus={planStatusForCard(turn.cardType)}
      resolved={turn.resolved}
      isPastGate={isPastGate}
      forceCollapsed={forceCollapsed}
      busy={busy}
      onResolve={onResolve}
      extraActions={extraActions}
    />
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * The plan status the card was emitted at. Reverse of expectedStatusForCard.
 * Used to drive the status pill in CardShell.
 */
function planStatusForCard(
  cardType: CardType
): import('./types').PlanStatus | null {
  return expectedStatusForCard(cardType);
}

/**
 * Map a ResolveAction's action verb to the local Turn.resolved label
 * used for past-state visualization.
 */
function resolvedLabelForAction(
  action: ResolveAction['action']
): 'confirmed' | 'cancelled' | 'edited' | 'amended' {
  switch (action) {
    case 'confirm':
      return 'confirmed';
    case 'cancel':
      return 'cancelled';
    case 'edit_field':
      return 'edited';
    case 'amend':
      return 'amended';
    case 'modify':
      // 'modify' routes through Owner; treat as 'confirmed' from the
      // current card's perspective — the next turn carries the response.
      return 'confirmed';
  }
}

/**
 * Walk forward through the turn list. A card at index `idx` is past-gate
 * when any later turn is a card with the same plan_id — i.e., a newer
 * card emission has superseded this one for the same plan. Includes
 * different card types (e.g., a result_card for the same plan counts
 * as a successor to the upstream Enumerator card).
 */
function hasNewerCardForPlan(turns: Turn[], idx: number): boolean {
  const target = turns[idx];
  if (target.kind !== 'card') return false;
  const targetPlanId = target.planId;
  if (!targetPlanId) return false;
  for (let i = idx + 1; i < turns.length; i++) {
    const t = turns[i];
    if (t.kind === 'card' && t.planId === targetPlanId) return true;
  }
  return false;
}

/**
 * Narrower variant: a later turn of the *same* card type for this plan.
 * Used by the sub-agent-param-card branch of the visibility rule, where
 * "draft superseded by edit" means a re-emission of the same card type —
 * not the eventual result_card, which is the gate's *output* (and should
 * not displace the preserved-collapsed param card).
 */
function hasNewerSameTypeCardForPlan(turns: Turn[], idx: number): boolean {
  const target = turns[idx];
  if (target.kind !== 'card') return false;
  const targetPlanId = target.planId;
  if (!targetPlanId) return false;
  for (let i = idx + 1; i < turns.length; i++) {
    const t = turns[i];
    if (
      t.kind === 'card' &&
      t.planId === targetPlanId &&
      t.cardType === target.cardType
    )
      return true;
  }
  return false;
}

/**
 * True when ANY card for the same plan_id exists after this turn.
 * Used by calc_progress hide: the ES two-gate flow emits an
 * indeterminate tracker during Gate 1→2 (hidden by the change_review
 * card) and a per-RI tracker during Gate 2→result (hidden by the
 * result_card). Generalizing from result_card-only covers both.
 */
function hasNewerCardForCalcProgress(turns: Turn[], idx: number): boolean {
  const target = turns[idx];
  if (!('planId' in target) || !target.planId) return false;
  const targetPlanId = target.planId;
  for (let i = idx + 1; i < turns.length; i++) {
    const t = turns[i];
    if (t.kind === 'card' && t.planId === targetPlanId) return true;
  }
  return false;
}

/**
 * True when a dag_progress row for the same plan_id exists after this
 * turn. Used to bridge the Enumerator-card → DAG-row transition: until
 * the DAG row arrives, the Enumerator card stays visible (past-state
 * styling) rather than vanishing instantly on Confirm.
 */
function hasDagProgressForPlan(turns: Turn[], idx: number): boolean {
  const target = turns[idx];
  if (target.kind !== 'card') return false;
  const targetPlanId = target.planId;
  if (!targetPlanId) return false;
  for (let i = idx + 1; i < turns.length; i++) {
    const t = turns[i];
    if (t.kind === 'dag_progress' && t.planId === targetPlanId) return true;
  }
  return false;
}

type PastVisibility = 'full' | 'collapsed' | 'hidden';

// Card-type buckets used by the past-visibility rule. Sub-agent param /
// HITL cards survive past-state as a collapsed reference (so the user
// can scroll back and inspect what params produced a result). Terminal
// result/explain cards stay fully shown. Everything else (Enumerator,
// scope proposals, ambiguity prompts) disappears once past — they are
// transient routing artifacts, not durable conversation content.
const SUB_AGENT_PARAM_CARDS = new Set<CardType>([
  'chart_visualization_param_card',
  'driver_analysis_param_card',
  'extracts_param_card',
  'edit_scenarios_input_selection_card',
  'edit_scenarios_change_review_card',
]);
const ALWAYS_FULL_CARDS = new Set<CardType>([
  'result_card',
  'explain_answer_card',
]);

const GATE_CARD_TYPES = new Set<CardType>([
  'plan_confirmation_card',
  ...SUB_AGENT_PARAM_CARDS,
]);

// Cross-plan sibling of hasNewerCardForPlan: true once a GATE_CARD_TYPES
// card for a DIFFERENT plan lands later. ambiguity_card is a valid target
// too, despite always having planId === null.
//
// Exported only so it could be unit-tested. That test (__tests__/hasNewerGateCard.test.ts) was DELETED on
// 2026-09-08 when poc merged into development: it had never executed on any branch. Importing it from this
// module pulls in useProfile -> AuthController -> services/API -> authConfig, which constructs an MSAL
// PublicClientApplication at module load; MSAL needs window.crypto.subtle, jsdom does not provide it, so the
// suite died at collection before any assertion. No CI workflow ran tests either, on any branch, so nothing
// ever reported it. Deleting beat keeping a test that implied coverage it did not provide.
//
// To test this function again, move it (and any peers worth covering) into a plain module with no React or
// auth imports — say AgenticV2/gateCards.ts — and import it back here. Testing it in place will hit the same
// MSAL wall. Front-end testing is currently a deliberate gap, not an oversight: see the Testing section of
// this repo's CLAUDE.md before adding the first test back.
export function hasNewerGateCard(turns: Turn[], idx: number): boolean {
  const target = turns[idx];
  if (target.kind !== 'card') return false;
  if (
    target.cardType !== 'ambiguity_card' &&
    !GATE_CARD_TYPES.has(target.cardType)
  )
    return false;
  const targetPlanId = target.planId ?? null;
  for (let i = idx + 1; i < turns.length; i++) {
    const t = turns[i];
    if (
      t.kind === 'card' &&
      GATE_CARD_TYPES.has(t.cardType) &&
      (t.planId ?? null) !== targetPlanId
    )
      return true;
  }
  return false;
}

/**
 * Decide how a past-state card should render. Active cards (no resolved
 * and no successor) bypass this rule and render full.
 *
 * The hide / collapse decision is keyed off *successors in the turn
 * list*, not the user's action mark alone. This avoids flicker on the
 * edit_field round-trip (the old card disappeared the moment the user
 * dispatched an edit, leaving a hole until the new emission arrived)
 * and on Enumerator → DAG handoff (the plan card vanished on Confirm
 * before the DAG row caught up). With successor-keyed hiding, the
 * card stays visible in its past-state styling until its replacement
 * actually lands.
 */
function pastVisibilityForCard(
  cardType: CardType,
  resolved: 'confirmed' | 'cancelled' | 'edited' | 'amended' | undefined,
  hasNewerCard: boolean,
  hasNewerSameTypeCard: boolean,
  hasNewerDag: boolean,
  hasNewerGateCard: boolean
): PastVisibility {
  if (ALWAYS_FULL_CARDS.has(cardType)) return 'full';
  if (SUB_AGENT_PARAM_CARDS.has(cardType)) {
    // Draft superseded by a re-emission of the same card type (the user
    // edited a field and the backend rebuilt the card): drop the draft
    // so the chat shows only the final form. The eventual result_card
    // is a different cardType and does NOT trigger this branch — the
    // preserved-collapsed param card stays as a reference above its
    // result.
    if (hasNewerSameTypeCard) return 'hidden';
    // Confirmed/cancelled is the user's final answer for this gate;
    // keep as a collapsed reference (chevron expands) so the user can
    // scroll back later and inspect the params that drove the result.
    if (resolved === 'confirmed' || resolved === 'cancelled')
      return 'collapsed';
    // A different plan's gate card superseded this one; collapse it.
    if (hasNewerGateCard) return 'collapsed';
    // Edited/amended without a successor yet means the user just
    // dispatched an in-card change and we are waiting for the backend
    // to re-emit. Keep the card fully rendered through the round-trip
    // so the UI doesn't flash collapsed-then-replaced. CardShell's
    // editsDisabled + opacity treatment already communicates "busy".
    return 'full';
  }
  // Plan-confirmation / scope-proposal / ambiguity: transient routing
  // artifacts. Hide once their successor lands (DAG row for a sub-agent
  // hand-off, or ANY newer card for the same plan — including the
  // downstream param card or result_card). Until then, keep visible
  // so Confirm doesn't leave a hole on screen.
  if (hasNewerCard || hasNewerDag) return 'hidden';
  // A different plan's gate card superseded this one; collapse it.
  if (hasNewerGateCard) return 'collapsed';
  return 'full';
}

function formatError(e: unknown): string {
  if (e instanceof HTTPError) {
    // 5xx messages from the agent stream can carry raw internals (e.g. a
    // full pydantic validation dump of malformed agent output). Never
    // render those to the user.
    if (e.status >= 500) {
      return (
        'Something went wrong while processing your request - most ' +
        'likely a temporary network or AI service issue. Please try again.'
      );
    }
    const body =
      e.body && typeof e.body === 'object'
        ? JSON.stringify(e.body)
        : String(e.body ?? '');
    return `${e.status} ${e.statusText}${body ? `: ${body}` : ''}`;
  }
  if (e instanceof Error) {
    return e.message;
  }
  return String(e);
}

export default AgenticV2;
